import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Download, FileText, Search, Image as ImageIcon, ChevronUp, ChevronDown, ArrowUpDown } from "lucide-react";
import api, { inr, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const CATS = ["nuts", "dry-fruits", "seeds", "berries", "gift-boxes", "exotic-collection", "corporate-gifts"];
const empty = () => ({
  name: "", category: "nuts", short_desc: "", description: "", origin: "", storage: "",
  images: [""], health_benefits: [""], best_seller: false, subscription: true,
  nutrition: { Energy: "", Protein: "", Fat: "", Carbohydrates: "", Fibre: "" },
  variants: [{ weight: "250g", price: 0, mrp: 0, stock: 100 }],
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [imgResults, setImgResults] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = () => {
    const header = "slug,name,category,short_desc,description,origin,storage,health_benefits,image_url,best_seller,variants,nutrition";
    const sample = ',California Almonds,nuts,Premium jumbo almonds,Hand-selected raw California almonds,California USA,Store in a cool dry place,Heart health;Rich in Vitamin E,,yes,100g:120:150:80;250g:264:312:60;500g:480:566:45,Energy=579 kcal;Protein=21 g';
    const sample2 = ',Cashew W320,nuts,Creamy whole cashews,Grade W320 whole cashews,Mangalore India,Keep airtight,Rich in healthy fats;Magnesium,,no,250g:340:400:60;500g:620:730:40,Energy=553 kcal;Protein=18 g';
    const blob = new Blob([[header, sample, sample2].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "aadvan_products_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doBulkUpload = async () => {
    if (!bulkFile) { toast.error("Please choose a CSV file first"); return; }
    setUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post("/admin/products/bulk", fd);
      setBulkResult(data);
      const total = (data.created || 0) + (data.updated || 0);
      if (total > 0) { toast.success(`${data.created} added, ${data.updated} updated`); load(); }
      else toast.error("Nothing imported — please check your CSV format.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const exportCatalog = async () => {
    try {
      const res = await api.get("/admin/products/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "aadvan_catalog.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Catalog exported to CSV");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const findImages = async () => {
    const q = (form.name || form.short_desc || form.category || "").trim();
    if (!q) { toast.error("Enter a product name first"); return; }
    setImgLoading(true);
    try {
      const { data } = await api.get("/admin/image-search", { params: { q, category: form.category } });
      setImgResults(data.images || []);
      if (!data.images || data.images.length === 0) toast("No image suggestions found");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImgLoading(false); }
  };

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setForm({ ...p, images: p.images.length ? p.images : [""], health_benefits: p.health_benefits.length ? p.health_benefits : [""] });
    setEditId(p.id); setImgResults([]); setOpen(true);
  };
  const startNew = () => { setForm(empty()); setEditId(null); setImgResults([]); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.variants[0].price) { toast.error("Name and price are required"); return; }
    const payload = {
      ...form,
      images: form.images.filter(Boolean),
      health_benefits: form.health_benefits.filter(Boolean),
      variants: form.variants.map((v) => ({ ...v, price: Number(v.price), mrp: Number(v.mrp || v.price), stock: Number(v.stock) })),
      slug: form.slug || "",
    };
    try {
      if (editId) { await api.put(`/admin/products/${editId}`, payload); toast.success("Product updated"); }
      else { await api.post("/admin/products", payload); toast.success("Product created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };

  const setVar = (idx, k, v) => setForm((f) => ({ ...f, variants: f.variants.map((vt, i) => i === idx ? { ...vt, [k]: v } : vt) }));
  const addVar = () => setForm((f) => ({ ...f, variants: [...f.variants, { weight: "", price: 0, mrp: 0, stock: 100 }] }));
  const rmVar = (idx) => setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  const totalStock = (p) => p.variants.reduce((s, v) => s + v.stock, 0);
  const isLowStock = (p) => p.variants.some((v) => v.stock <= 10);
  const q = query.trim().toLowerCase();
  let filtered = products.filter((p) => {
    const matchesCat = catFilter === "all" || p.category === catFilter;
    const matchesQuery = !q || p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) || (p.short_desc || "").toLowerCase().includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q));
    return matchesCat && matchesQuery && (!lowStockOnly || isLowStock(p));
  });
  if (sortKey) {
    const dir = sortDir === "asc" ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === "price") { av = a.variants[0].price; bv = b.variants[0].price; }
      else if (sortKey === "stock") { av = totalStock(a); bv = totalStock(b); }
      else { av = (a[sortKey] || "").toLowerCase(); bv = (b[sortKey] || "").toLowerCase(); }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }
  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const sortIcon = (col) => sortKey !== col
    ? <ArrowUpDown size={12} className="inline ml-1 opacity-40" />
    : sortDir === "asc" ? <ChevronUp size={12} className="inline ml-1" /> : <ChevronDown size={12} className="inline ml-1" />;

  return (
    <div className="fade-in" data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl text-forest">Products</h1><p className="text-muted-foreground text-sm">{filtered.length} of {products.length} products</p></div>
        <div className="flex gap-3">
          <Button onClick={exportCatalog} data-testid="export-catalog-button" variant="outline" className="border-forest text-forest hover:bg-forest hover:text-cream rounded-full"><Download size={16} className="mr-1" /> Export CSV</Button>
          <Button onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkFile(null); }} data-testid="bulk-upload-button" variant="outline" className="border-forest text-forest hover:bg-forest hover:text-cream rounded-full"><Upload size={16} className="mr-1" /> Bulk Upload CSV</Button>
          <Button onClick={startNew} data-testid="add-product-button" className="bg-forest text-cream rounded-full"><Plus size={16} className="mr-1" /> Add Product</Button>
        </div>
      </div>

      <div className="mb-5 space-y-3">
        <div className="relative w-full">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, category or tag…" data-testid="admin-product-search" className="w-full pl-12 pr-11 h-14 text-lg rounded-full bg-white text-forest border-2 focus-visible:ring-2 focus-visible:ring-gold" />
          {query && <button onClick={() => setQuery("")} data-testid="admin-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-forest"><X size={18} /></button>}
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...CATS].map((c) => (
            <button key={c} onClick={() => setCatFilter(c)} data-testid={`admin-filter-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs capitalize transition-colors ${catFilter === c ? "bg-forest text-cream" : "bg-sand text-forest hover:bg-gold/20"}`}>
              {c === "all" ? "All" : c.replace("-", " ")}
            </button>
          ))}
          <button onClick={() => setLowStockOnly((v) => !v)} data-testid="lowstock-toggle"
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${lowStockOnly ? "bg-destructive text-white" : "bg-sand text-forest hover:bg-gold/20"}`}>
            Low stock only
          </button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-forest text-cream text-left"><tr>
            <th className="p-4 cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="sort-name">Product{sortIcon("name")}</th>
            <th className="p-4 cursor-pointer select-none" onClick={() => toggleSort("category")} data-testid="sort-category">Category{sortIcon("category")}</th>
            <th className="p-4 cursor-pointer select-none" onClick={() => toggleSort("price")} data-testid="sort-price">Price{sortIcon("price")}</th>
            <th className="p-4 cursor-pointer select-none" onClick={() => toggleSort("stock")} data-testid="sort-stock">Stock{sortIcon("stock")}</th>
            <th className="p-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr data-testid="admin-no-results"><td colSpan={5} className="p-10 text-center text-muted-foreground">No products match "{query}"{catFilter !== "all" ? ` in ${catFilter.replace("-", " ")}` : ""}.</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} className="border-b border-border" data-testid={`product-row-${p.slug}`}>
                <td className="p-4 flex items-center gap-3"><img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover bg-sand" /><span className="text-forest">{p.name}</span></td>
                <td className="p-4 capitalize text-muted-foreground">{p.category.replace("-", " ")}</td>
                <td className="p-4">{inr(p.variants[0].price)}</td>
                <td className={`p-4 ${isLowStock(p) ? "text-destructive font-medium" : ""}`}>{totalStock(p)}</td>
                <td className="p-4 text-right">
                  <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="text-forest hover:text-gold mr-3"><Pencil size={16} /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-${p.slug}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()} data-testid="product-form" role="dialog" aria-modal="true" aria-label={editId ? "Edit product" : "New product"}>
            <div className="sticky top-0 bg-forest text-cream px-6 py-4 flex justify-between items-center">
              <h2 className="font-serif text-2xl">{editId ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Input placeholder="Product Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pf-category" className="border border-border rounded-lg px-3 py-2 text-sm">
                  {CATS.map((c) => <option key={c} value={c}>{c.replace("-", " ")}</option>)}
                </select>
                <Input placeholder="Origin (e.g. California, USA)" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="pf-origin" />
              </div>
              <Input placeholder="Short description" value={form.short_desc} onChange={(e) => setForm({ ...form, short_desc: e.target.value })} data-testid="pf-short" />
              <textarea placeholder="Full description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-border rounded-lg p-3 text-sm" data-testid="pf-desc" />
              <div>
                <div className="flex gap-2">
                  <Input placeholder="Image URL (or click Find Images →)" value={form.images[0]} onChange={(e) => setForm({ ...form, images: [e.target.value, ...form.images.slice(1)] })} data-testid="pf-image" />
                  <Button type="button" onClick={findImages} disabled={imgLoading} data-testid="pf-find-images" variant="outline" className="border-forest text-forest hover:bg-forest hover:text-cream rounded-lg whitespace-nowrap px-4"><Search size={15} className="mr-1" /> {imgLoading ? "Searching…" : "Find Images"}</Button>
                </div>
                {form.images[0] ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><ImageIcon size={13} /> Selected image preview:
                    <img src={form.images[0]} alt="preview" className="w-16 h-16 rounded-lg object-cover bg-sand ml-1" />
                  </div>
                ) : null}
                {imgResults.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-1.5">Click a photo to use it:</div>
                    <div className="grid grid-cols-4 gap-2" data-testid="image-suggestions">
                      {imgResults.map((u, i) => (
                        <button type="button" key={i} data-testid={`image-option-${i}`} onClick={() => setForm({ ...form, images: [u, ...form.images.slice(1)] })}
                          className={`relative rounded-lg overflow-hidden border-2 transition-colors ${form.images[0] === u ? "border-gold" : "border-transparent hover:border-gold/50"}`}>
                          <img src={u} alt="" className="w-full h-16 object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Input placeholder="Storage instructions" value={form.storage} onChange={(e) => setForm({ ...form, storage: e.target.value })} data-testid="pf-storage" />
              <Input placeholder="Health benefits (comma separated)" value={form.health_benefits.join(", ")} onChange={(e) => setForm({ ...form, health_benefits: e.target.value.split(",").map((s) => s.trim()) })} data-testid="pf-benefits" />

              <div>
                <div className="flex justify-between items-center mb-2"><span className="text-sm font-medium text-forest">Variants</span><button onClick={addVar} className="text-xs text-gold" data-testid="pf-add-variant">+ Add variant</button></div>
                {form.variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 mb-2 items-center">
                    <Input placeholder="Weight" value={v.weight} onChange={(e) => setVar(i, "weight", e.target.value)} data-testid={`pf-var-weight-${i}`} />
                    <Input placeholder="Price" type="number" value={v.price} onChange={(e) => setVar(i, "price", e.target.value)} data-testid={`pf-var-price-${i}`} />
                    <Input placeholder="MRP" type="number" value={v.mrp} onChange={(e) => setVar(i, "mrp", e.target.value)} />
                    <div className="flex gap-1"><Input placeholder="Stock" type="number" value={v.stock} onChange={(e) => setVar(i, "stock", e.target.value)} data-testid={`pf-var-stock-${i}`} />{form.variants.length > 1 && <button onClick={() => rmVar(i)} className="text-destructive"><X size={16} /></button>}</div>
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.best_seller} onChange={(e) => setForm({ ...form, best_seller: e.target.checked })} className="accent-forest" data-testid="pf-bestseller" /> Mark as Best Seller</label>

              <Button onClick={save} data-testid="pf-save" className="w-full bg-gold text-forest hover:bg-gold-soft rounded-full h-11">{editId ? "Update Product" : "Create Product"}</Button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal" role="dialog" aria-modal="true" aria-label="Bulk upload products">
            <div className="sticky top-0 bg-forest text-cream px-6 py-4 flex justify-between items-center">
              <h2 className="font-serif text-2xl">Bulk Upload Products</h2>
              <button onClick={() => setBulkOpen(false)} data-testid="bulk-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-sand rounded-xl p-4 text-sm text-muted-foreground space-y-2">
                <p className="text-forest font-medium">How it works</p>
                <p>1. Download the CSV template below and fill in your products.</p>
                <p>2. Columns: <span className="text-forest">name, category, short_desc, description, origin, storage, health_benefits, image_url, best_seller, variants, nutrition</span>.</p>
                <p>3. <span className="text-forest">category</span> must be one of: nuts, dry-fruits, seeds, berries, gift-boxes, exotic-collection, corporate-gifts.</p>
                <p>4. <span className="text-forest">variants</span> format: <code className="text-xs bg-white px-1 rounded">weight:price:mrp:stock;weight:price:mrp:stock</code></p>
                <p>5. Separate multiple <span className="text-forest">health_benefits</span> with <code className="text-xs bg-white px-1 rounded">;</code> and nutrition as <code className="text-xs bg-white px-1 rounded">Key=Value;Key=Value</code>.</p>
              </div>

              <Button onClick={downloadTemplate} data-testid="download-template" variant="outline" className="w-full border-forest text-forest hover:bg-forest hover:text-cream rounded-lg"><Download size={16} className="mr-2" /> Download CSV Template</Button>

              <label className="block border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-gold transition-colors" data-testid="bulk-dropzone">
                <input type="file" accept=".csv" className="hidden" data-testid="bulk-file-input" onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }} />
                <FileText size={28} className="text-gold mx-auto mb-2" />
                <div className="text-sm text-forest">{bulkFile ? bulkFile.name : "Click to choose a CSV file"}</div>
                <div className="text-xs text-muted-foreground mt-1">Only .csv files are supported</div>
              </label>

              <Button onClick={doBulkUpload} disabled={uploading || !bulkFile} data-testid="bulk-submit" className="w-full bg-gold text-forest hover:bg-gold-soft rounded-full h-11">
                <Upload size={16} className="mr-2" /> {uploading ? "Uploading…" : "Upload & Import"}
              </Button>

              {bulkResult && (
                <div className="rounded-xl border border-border p-4 text-sm" data-testid="bulk-result">
                  <p className="text-forest font-medium">Import complete</p>
                  <p className="text-green-700">✓ {bulkResult.created} added · {bulkResult.updated || 0} updated</p>
                  {bulkResult.failed > 0 && <p className="text-destructive">✗ {bulkResult.failed} row(s) failed</p>}
                  {bulkResult.errors && bulkResult.errors.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-destructive max-h-32 overflow-y-auto no-scrollbar">
                      {bulkResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
